// MessageSuggestionsContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';

// Create the context
const MessageSuggestionsContext = createContext();

// Custom hook to use the context
export const useMessageSuggestionsContext = () => {
    const context = useContext(MessageSuggestionsContext);
    if (!context) {
        throw new Error('useMessageSuggestionsContext must be used within a MessageSuggestionsProvider');
    }
    return context;
};

// Provider component
export const MessageSuggestionsProvider = ({ children }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentCustomerMessage, setCurrentCustomerMessage] = useState('');
    const [currentUserLineId, setCurrentUserLineId] = useState(null);
    const [currentAdminMessage, setCurrentAdminMessage] = useState(null);

    // Main function to get suggestions
    const getSuggestions = useCallback(async (customerMessage, userLineId = null, limit = 5, adminMessage = null) => {
        if (!customerMessage?.trim()) {
            setSuggestions([]);
            setCurrentCustomerMessage('');
            setCurrentUserLineId(null);
            setCurrentAdminMessage(null);
            return;
        }

        setLoading(true);
        setError(null);
        setCurrentCustomerMessage(customerMessage);
        setCurrentUserLineId(userLineId);
        setCurrentAdminMessage(adminMessage);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/suggestion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                },
                body: JSON.stringify({
                    message: customerMessage,
                    user_line_id: userLineId,
                    admin_message: adminMessage,
                    limit: limit,
                    options: {
                        min_relevance: 0.1
                    }
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setSuggestions(data.suggestions);
            } else {
                throw new Error(data.error || 'Failed to get suggestions');
            }
        } catch (err) {
            setError(err.message);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Function to clear suggestions
    const clearSuggestions = useCallback(() => {
        setSuggestions([]);
        setCurrentCustomerMessage('');
        setCurrentUserLineId(null);
        setCurrentAdminMessage(null);
        setError(null);
        setLoading(false);
    }, []);

    // Function to refresh suggestions with current parameters
    const refreshSuggestions = useCallback(() => {
        if (currentCustomerMessage) {
            getSuggestions(currentCustomerMessage, currentUserLineId, 5, currentAdminMessage);
        }
    }, [currentCustomerMessage, currentUserLineId, currentAdminMessage, getSuggestions]);

    // Function to update suggestions manually (useful for real-time updates)
    const updateSuggestions = useCallback((newSuggestions) => {
        setSuggestions(newSuggestions);
    }, []);

    // Function to add a new suggestion to the list
    const addSuggestion = useCallback((newSuggestion) => {
        setSuggestions(prev => [...prev, newSuggestion]);
    }, []);

    // Function to remove a suggestion by index
    const removeSuggestion = useCallback((index) => {
        setSuggestions(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Function to get suggestion by index
    const getSuggestionByIndex = useCallback((index) => {
        return suggestions[index] || null;
    }, [suggestions]);

    // Function to check if suggestions are available
    const hasSuggestions = useCallback(() => {
        return suggestions.length > 0;
    }, [suggestions]);

    const contextValue = {
        // State
        suggestions,
        loading,
        error,
        currentCustomerMessage,
        currentUserLineId,
        currentAdminMessage,

        // Actions
        getSuggestions,
        clearSuggestions,
        refreshSuggestions,
        updateSuggestions,
        addSuggestion,
        removeSuggestion,

        // Utilities
        getSuggestionByIndex,
        hasSuggestions,
        suggestionsCount: suggestions.length,
    };

    return (
        <MessageSuggestionsContext.Provider value={contextValue}>
            {children}
        </MessageSuggestionsContext.Provider>
    );
};

// HOC for components that need suggestions
export const withMessageSuggestions = (Component) => {
    return function WrappedComponent(props) {
        const suggestionsContext = useMessageSuggestionsContext();
        return <Component {...props} suggestions={suggestionsContext} />;
    };
};

// Utility hook for debounced suggestions
export const useDebouncedSuggestions = (delay = 500) => {
    const { getSuggestions } = useMessageSuggestionsContext();
    const [timeoutId, setTimeoutId] = useState(null);

    const debouncedGetSuggestions = useCallback((customerMessage, userLineId = null, limit = 5, adminMessage = null) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        const newTimeoutId = setTimeout(() => {
            getSuggestions(customerMessage, userLineId, limit, adminMessage);
        }, delay);

        setTimeoutId(newTimeoutId);
    }, [getSuggestions, delay, timeoutId]);

    return debouncedGetSuggestions;
};